import { type AntdIconProps } from '@ant-design/icons/lib/components/AntdIcon';
import { css, keyframes } from '@emotion/react';

import AntdTokens from './antd-tokens/light.json' with { type: 'json' };

import type { CSSInterpolation } from '@emotion/serialize';
import type { EmptyProps, SelectProps, ThemeConfig } from 'antd';

// TODO - consider moving to a separate file as described in our Notion's "Styling" page
const { padding, paddingMD, paddingSM, paddingS, paddingXS, paddingXXS, paddingLG, paddingXL, paddingXXL } = AntdTokens;

// TODO - apply Nick's notes about this file from here:
// https://github.com/Navina-ai/summary-frontend/pull/1353/files#diff-199c072c046a1708097c9654913d55ca83ca8c22d8a74ed7bfb9f92e5adea4ef

const paddings = {
	xxxs: `2px` as const,
	/* eslint-disable @typescript-eslint/consistent-type-assertions -- `as const` only works on literal values, which AntD doesn't supply :( */
	xxs: `${paddingXXS}px` as '4px',
	xs: `${paddingXS}px` as '8px',
	s: `${paddingS}px` as '10px',
	sm: `${paddingSM}px` as '12px',
	base: `${padding}px` as '16px',
	md: `${paddingMD}px` as '20px',
	lg: `${paddingLG}px` as '24px',
	xl: `${paddingXL}px` as '32px',
	xxl: `${paddingXXL}px` as '48px',
	/* eslint-enable @typescript-eslint/consistent-type-assertions */
} as const;

const { margin, marginSM, marginXS, marginXXS, marginLG, marginXL } = AntdTokens;

const margins = {
	xxxs: '2px' as const,
	/* eslint-disable @typescript-eslint/consistent-type-assertions -- `as const` only works on literal values, which AntD doesn't supply :( */
	xxs: `${marginXXS}px` as '4px',
	xs: `${marginXS}px` as '8px',
	sm: `${marginSM}px` as '12px',
	base: `${margin}px` as '16px',
	md: `${margin}px` as '20px',
	lg: `${marginLG}px` as '24px',
	xl: `${marginXL}px` as '48px',
	/* eslint-enable @typescript-eslint/consistent-type-assertions */
} as const;

// "purple.1": "#EFEDF7",
// "purple.2": "#C8C1FA",
// "purple.3": "#ACA1F7",
// "purple.4": "#9082F4",
// "purple.5": "#7563F2",
// "purple.6": "#5944EF",
// "purple.7": "#4A39C7",
// "purple.8": "#3B2D9F",
// "purple.9": "#2D2278",
// "purple.10": "#1E1750",
const {
	'purple.1': purple1,
	'purple.2': purple2,
	'purple.3': purple3,
	'purple.4': purple4,
	'purple.5': purple5,
	'purple.6': purple6,
	'purple.7': purple7,
	'purple.8': purple8,
	'purple.9': purple9,
	'purple.10': purple10,
	'blue.1': blue1,
	'blue.2': blue2,
	'blue.3': blue3,
	'blue.4': blue4,
	'blue.5': blue5,
	'blue.6': blue6,
	'blue.7': blue7,
	'blue.8': blue8,
	'blue.9': blue9,
	'blue.10': blue10,
	'cyan.3': cyan3,
	colorError,
	colorErrorText,
	colorInfo,
	colorSuccess,
	colorErrorBg,
	colorSuccessTextActive,
	colorSuccessBgHover,
	colorSuccessActive,
	colorSuccessBg,
	colorSuccessBorderHover,
	colorLinkActive,
	colorFillQuaternary,
	colorSuccessBorder,
	colorBgSpotlight,
	colorFill,
	colorBgLayout,
	colorWarningBg,
	colorWarningBorder,
	colorWarningTextActive,
	colorLink,
	controlItemBgHover,
} = AntdTokens;

const colors = {
	// Primary
	// Secondary
	// Dark / Light
	black1: 'rgba(0, 0, 0, 0.88)',
	black2: 'rgba(0, 0, 0, 0.45)',
	black3: 'rgba(0, 0, 0, 0.6)',
	white: 'white',
	whiteTransparent: 'rgba(255, 255, 255, 0.7)',
	lightGrey: 'rgba(250, 250, 250, 1)',
	purple1: purple1,
	purple2: purple2,
	purple3: purple3,
	purple4: purple4,
	purple5: purple5,
	purple6: purple6,
	purple7: purple7,
	purple8: purple8,
	purple9: purple9,
	purple10: purple10,
	blue1: blue1,
	blue2: blue2,
	blue3: blue3,
	blue4: blue4,
	blue5: blue5,
	blue6: blue6,
	blue7: blue7,
	blue8: blue8,
	blue9: blue9,
	blue10: blue10,
	colorBgLayout,
	colorError: colorError,
	colorErrorText: colorErrorText,
	colorInfo: colorInfo,
	colorSuccess: colorSuccess,
	colorErrorBg: colorErrorBg,
	colorSuccessBg: colorSuccessBg,
	colorSuccessBorderHover: colorSuccessBorderHover,
	colorWarning: '#faad14',
	primary: '#322b5f',
	barLevelLow: '#E0E0E0',
	barLevelMedium: '#C3BCE2',
	barLevelHigh: '#F3982E',
	barLevelExtreme: '#FF6239',
	grayedOut: '#F0F0F0',
	controlItemBgHover,
	colorSuccessBgHover: colorSuccessBgHover,
	colorSuccessActive: colorSuccessActive,
	colorSuccessTextActive: colorSuccessTextActive,
	colorSuccessBorder: colorSuccessBorder,
	colorFill: colorFill,
	colorWarningBg: colorWarningBg,
	colorWarningBorder: colorWarningBorder,
	colorLinkActive,
	colorFillQuaternary,
	colorBgSpotlight,
	cyan3,
	colorTextDisabled: 'rgba(0, 0, 0, 0.25)',
	colorBgContainerDisabled: 'rgba(0, 0, 0, 0.04)',
	colorWarningTextActive: colorWarningTextActive,
	colorLink: colorLink,
	rowHoverBg: '#fafafa',
	colorScrollbar: '#d9d9d9',
	colorCardContainer: '#bdbdbd',
	selection: '#d2d2ff',
} as const;

const gradients = {
	autoDoc: 'linear-gradient(89.68deg, #13c2c2 -9.32%, #5944ef 35.8%, #332789 99.81%)',
} as const;

const {
	fontSizeSM,
	fontSize,
	fontSizeLG,
	fontSizeXL,
	fontSizeHeading1,
	fontSizeHeading2,
	fontSizeHeading3,
	fontSizeHeading4,
	fontSizeHeading5,
	sizeXXL,
} = AntdTokens;

const fontSizes = {
	xs: `10px`,
	/* eslint-disable @typescript-eslint/consistent-type-assertions -- `as const` only works on literal values, which AntD doesn't supply :( */
	sm: `${fontSizeSM}px` as '12px',
	base: `${fontSize}px` as '14px',
	lg: `${fontSizeLG}px` as '16px',
	xl: `${fontSizeXL}px` as '20px',
	title1: `${fontSizeHeading1}px` as '38px',
	title2: `${fontSizeHeading2}px` as '30px',
	title3: `${fontSizeHeading3}px` as '24px',
	title4: `${fontSizeHeading4}px` as '20px',
	title5: `${fontSizeHeading5}px` as '16px',
	iconXXL: `${sizeXXL}px` as '48px',
	/* eslint-enable @typescript-eslint/consistent-type-assertions */
} as const;

const { fontFamily } = AntdTokens;

const fontFamilies = {
	base: fontFamily,
} as const;

const fontLineHeights = {
	base: 1.5,
	s: 1.25,
} as const;

const {
	colorText,
	colorPrimaryText,
	colorPrimaryTextActive,
	colorTextSecondary,
	colorTextTertiary,
	colorTextQuaternary,
} = AntdTokens;

const fontColors = {
	base: colorText,
	primary: colorPrimaryText,
	primaryActive: colorPrimaryTextActive,
	secondary: colorTextSecondary,
	secondaryDarkBackgroundContrast: 'rgba(255, 255, 255, 0.62)',
	/* eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- `as const` only works on literal values, which AntD doesn't supply :( */
	tertiary: colorTextTertiary as 'rgba(0, 0, 0, 0.45)',
	quaternary: colorTextQuaternary,
	primaryDarkBackgroundContrast: colors.white,
	tertiaryDarkBackgroundContrast: 'rgba(255, 255, 255, 0.45)',
} as const;

const { fontWeightNormal, fontWeightBold, fontWeightBolder, fontWeightStrong } = AntdTokens;

const fontWeights = {
	base: fontWeightNormal,
	medium: fontWeightBold,
	semibold: fontWeightBolder,
	bold: fontWeightStrong,
	500: 500,
	600: 600,
} as const;

const { boxShadow, boxShadowSecondary } = AntdTokens;

const boxShadows = {
	base: boxShadow,
	secondary: boxShadowSecondary,
	dropdown: '0px 9px 28px 8px #0000000d, 0px 3px 6px -4px #0000001f, 0px 6px 16px 0px #00000014' as const,
} as const;

const { colorBorder, colorBorderSecondary } = AntdTokens;
const borderColors = {
	/* eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- `as const` only works on literal values, which AntD doesn't supply :( */
	base: colorBorder as 'rgba(0, 0, 0, 0.15)',
	/* eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- `as const` only works on literal values, which AntD doesn't supply :( */
	secondary: colorBorderSecondary as 'rgba(0, 0, 0, 0.06)',
	neutral: '#BDBDBD',
	error: '#FFCCC7',
} as const;

const { borderRadius, borderRadiusXXS, borderRadiusXS, borderRadiusSM, borderRadiusLG, borderRadiusOuter } = AntdTokens;
const borderRadii = {
	/* eslint-disable @typescript-eslint/consistent-type-assertions -- `as const` only works on literal values, which AntD doesn't supply :( */
	xxs: `${borderRadiusXXS}px` as '1px',
	xs: `${borderRadiusXS}px` as '2px',
	sm: `${borderRadiusSM}px` as '4px',
	base: `${borderRadius}px` as '6px',
	lg: `${borderRadiusLG}px` as '8px',
	xl: `${borderRadiusLG * 2}px` as '16px',
	xxl: `${borderRadiusLG * 3}px` as '24px',
	outer: `${borderRadiusOuter}px` as '4px',
	/* eslint-enable @typescript-eslint/consistent-type-assertions */
} as const;

const outlines = {
	/* eslint-disable @typescript-eslint/consistent-type-assertions -- `as const` only works on literal values, which AntD doesn't supply :( */
	base: `1px solid ${colorBorder}` as '1px solid rgba(0, 0, 0, 0.15)',
	secondary: `1px solid ${colorBorderSecondary}` as '1px solid rgba(0, 0, 0, 0.06)',
	/* eslint-enable @typescript-eslint/consistent-type-assertions */
} as const;

const zIndex = {
	natural: 1,
	hccRecommendationStickyHeader: 2,
	headerBottomPanel: 3,
	feedbackButton: 203,
	bugReport: 999999999,
} as const;

const { controlHeight, controlHeightSM, controlHeightXS, controlHeightLG } = AntdTokens;

const controlHeights = {
	/* eslint-disable @typescript-eslint/consistent-type-assertions -- `as const` only works on literal values, which AntD doesn't supply :( */
	base: `${controlHeight}px` as '32px',
	sm: `${controlHeightSM}px` as '24px',
	xs: `${controlHeightXS}px` as '16px',
	lg: `${controlHeightLG}px` as '40px',
	/* eslint-enable @typescript-eslint/consistent-type-assertions */
} as const;

const { tableInlineSizesXS, tableInlineSizesS, tableInlineSizesSM, tableInlineSizesM, tableInlineSizesLG } = AntdTokens;

const tableInlineSizes = {
	/* eslint-disable @typescript-eslint/consistent-type-assertions -- `as const` only works on literal values, which AntD doesn't supply :( */
	xs: `${tableInlineSizesXS}px` as '40px',
	s: `${tableInlineSizesS}px` as '114px',
	sm: `${tableInlineSizesSM}px` as '120px',
	m: `${tableInlineSizesM}px` as '150px',
	lg: `${tableInlineSizesLG}px` as '200px',
	/* eslint-enable @typescript-eslint/consistent-type-assertions */
} as const;

const baseTheme = {
	paddings,
	margins,
	colors,
	gradients,
	font: {
		sizes: fontSizes,
		families: fontFamilies,
		colors: fontColors,
		weights: fontWeights,
		lineHeights: fontLineHeights,
	},
	boxShadows,
	border: {
		colors: borderColors,
		radii: borderRadii,
	},
	tooltipInnerStyle: {
		paddingBlock: paddings.sm,
		paddingInline: paddings.lg,
	},
	outlines,
	zIndex,
	controlHeights,
	tableInlineSizes,
} as const;

const lightTheme = { ...baseTheme } as const satisfies typeof baseTheme;

/**
 * Currently, we have only light theme. Dark theme is planned for the future.
 */
// const darkTheme = { ...baseTheme } as const satisfies typeof baseTheme;
// const htmlDataThemeAttribute = document.querySelector('html')?.getAttribute('data-theme');

export const theme = lightTheme;

export const baseTextStyle = css`
	font-family: ${theme.font.families.base};
	line-height: ${theme.font.lineHeights.base};
	color: ${theme.font.colors.base};
	font-size: ${theme.font.sizes.base};
`;

export const secondaryTextStyle = css`
	${baseTextStyle}
	color: ${theme.font.colors.secondary};
`;

export const quaternaryTextStyle = css`
	${baseTextStyle}
	color: ${theme.font.colors.quaternary};
`;

export const disabledTextStyle = css`
	${baseTextStyle}
	color: ${theme.colors.colorTextDisabled};
`;

export const mediumTextStyle = css`
	${baseTextStyle}
	font-weight: ${theme.font.weights.medium};
`;

export const boldTextStyle = css`
	${baseTextStyle}
	font-weight: ${theme.font.weights[600]};
`;

export const TWO_TONE_ICON_SETTINGS = {
	twoToneColor: [theme.colors.purple6, theme.colors.purple1],
} as const satisfies AntdIconProps;

export const TWO_TONE_GRAYED_ICON_SETTINGS = {
	...TWO_TONE_ICON_SETTINGS,
	style: { filter: 'grayscale(100%)', opacity: 0.7 },
} as const satisfies AntdIconProps;

export const EMPTY_ICON_SETTINGS = {
	styles: { image: { fontSize: 44, height: 60 } },
} as const satisfies Partial<EmptyProps>;

// TODO - consider move to "with" file
const COMMON_SELECT_PROPS = {
	showAction: ['focus', 'click'],
	style: { inlineSize: '100%' },
	showSearch: true,
} as const satisfies Partial<SelectProps>;

export const NON_DATA_SOURCE_SELECT_PROPS = {
	...COMMON_SELECT_PROPS,
	mode: 'multiple',
	allowClear: true,
} as const satisfies Partial<SelectProps>;

export const ANTD_COMPONENTS_CONFIG = {
	Select: {
		optionSelectedBg: theme.colors.purple1,
		optionActiveBg: `${theme.colors.purple1}99`, // 60% opacity
	},
} as const satisfies ThemeConfig['components'];

export const REMOVE_ANTD_TAG_MARGIN = css`
	margin-inline-end: 0 !important;
`;

const STANDARD_WIDTH = 1920;
const MEDIUM_WIDTH = 1460;
const LOWEST_WIDTH = 1200;

function getMediaStatement(query: string): (style: CSSInterpolation) => ReturnType<typeof css> {
	return (style: CSSInterpolation): ReturnType<typeof css> => css`
		@media ${query} {
			${style}
		}
	`;
}

const MEDIA_QUERY = {
	max: function getMaxMediaStatement(width: number) {
		return `(max-width: ${width}px)` as const;
	},
	min: function getMinMediaStatement(width: number) {
		return `(min-width: ${width}px)` as const;
	},
} as const;

/**
 * Ordering matters only when queries overlap:
 *
 * - max-width queries overlap: `onExtremelyNarrowScreen` (<=1200) also matches `onNarrowScreen` (<=1460).
 *   If both are used, put `onExtremelyNarrowScreen` AFTER `onNarrowScreen` so it overrides.
 *
 * - min-width queries overlap: `onExtremelyWideScreen` (>=1921) also matches `onWideScreen` (>=1461).
 *   If both are used, put `onExtremelyWideScreen` AFTER `onWideScreen` so it overrides.
 *
 * Example:
 *   ${onNarrowScreen(...)}
 *   ${onExtremelyNarrowScreen(...)}
 *   ${onWideScreen(...)}
 *   ${onExtremelyWideScreen(...)}
 */

/** <= 1200px. Overrides `onNarrowScreen` when declared after it. */
const onExtremelyNarrowScreen = getMediaStatement(MEDIA_QUERY.max(LOWEST_WIDTH));

/** <= 1460px (includes extremely narrow). Put before `onExtremelyNarrowScreen` if both are used. */
const onNarrowScreen = getMediaStatement(MEDIA_QUERY.max(MEDIUM_WIDTH));

/** >= 1461px */
const onWideScreen = getMediaStatement(MEDIA_QUERY.min(MEDIUM_WIDTH + 1));

/** >= 1921px. Overrides `onWideScreen` when declared after it. */
const onExtremelyWideScreen = getMediaStatement(MEDIA_QUERY.min(STANDARD_WIDTH + 1));

const _BREAKPOINT_KEYS = ['narrow', 'extremelyNarrow', 'wide', 'extremelyWide'] as const;
type BreakpointKey = (typeof _BREAKPOINT_KEYS)[number];

export function responsive({
	narrow,
	extremelyNarrow,
	wide,
	extremelyWide,
}: Partial<Record<BreakpointKey, CSSInterpolation>>) {
	return css`
		${narrow ? onNarrowScreen(narrow) : ''}
		${extremelyNarrow ? onExtremelyNarrowScreen(extremelyNarrow) : ''}
    ${wide ? onWideScreen(wide) : ''}
    ${extremelyWide ? onExtremelyWideScreen(extremelyWide) : ''}
	`;
}

const PulseAnimation = keyframes`
	0%, 100% {
		opacity: 1;
	}
	50% {
		opacity: 0.4;
	}
`;

export const withSlowPulseAnimation = css`
	animation: ${PulseAnimation} 1.5s ease-in-out infinite;
`;
